/**
 * ContactSalesPage - Enterprise Sales Contact Form
 * 
 * Dark themed sales inquiry form with value props on the left
 * and form on the right. Sends email via Resend.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  ShieldCheck, 
  BrainCircuit, 
  Users, 
  ChevronDown,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import api from '../services/api';

const ContactSalesPage = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company_name: '',
    company_size: '',
    team_function: '',
    biggest_blocker: '',
    additional_details: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await api.post('/api/contact-sales/submit', formData);
      setIsSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success/Acknowledgement Screen
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#030303] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Thank You!</h1>
          <p className="text-gray-400 mb-8 leading-relaxed">
            We've received your inquiry and will be in touch within 24 hours. 
            In the meantime, feel free to explore our features or start a free trial.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/features">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 w-full sm:w-auto">
                Explore Features
              </Button>
            </Link>
            <Link to="/#pricing">
              <Button className="bg-white text-black hover:bg-gray-200 w-full sm:w-auto">
                Start Free Trial
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Simple Nav */}
      <nav className="border-b border-white/5 bg-black/50 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <span className="text-white font-bold text-sm">SR</span>
            </div>
            <span className="text-lg font-semibold text-white tracking-tight">
              StackRanked.ai
            </span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Main Content Split */}
      <main className="flex-1 flex flex-col lg:flex-row max-w-7xl mx-auto w-full">
        
        {/* Left Column: Value Props */}
        <div className="lg:w-1/2 p-8 lg:p-16 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-white/5">
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 text-purple-400 text-xs font-medium border border-purple-500/20 mb-8">
              Enterprise Edition
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
              Scale Product Love <br />
              <span className="text-gray-500">Across the Org.</span>
            </h1>
            
            <p className="text-lg text-gray-400 mb-10 leading-relaxed">
              Empower your entire organization with the same AI-driven context that makes individual PMs 10x faster. We help large teams shift from "feature factories" to strategic powerhouses.
            </p>

            {/* Value Bullets */}
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="mt-1 w-8 h-8 rounded bg-white/5 border border-white/10 flex items-center justify-center text-purple-400 flex-shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Enterprise-Grade Security</h3>
                  <p className="text-sm text-gray-500">SOC2 Type II compliant, SSO/SAML enforcement (Okta, Azure AD), and optional on-premise deployment for sensitive data.</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="mt-1 w-8 h-8 rounded bg-white/5 border border-white/10 flex items-center justify-center text-purple-400 flex-shrink-0">
                  <BrainCircuit className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Custom Context Models</h3>
                  <p className="text-sm text-gray-500">We train a private instance of our AI on your historical Confluence, SharePoint, and Google Drive data. Your history becomes your leverage.</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="mt-1 w-8 h-8 rounded bg-white/5 border border-white/10 flex items-center justify-center text-purple-400 flex-shrink-0">
                  <Users className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">White-Glove Migration</h3>
                  <p className="text-sm text-gray-500">Dedicated Success Managers help you migrate complex Jira backlogs and map them to our strategic hierarchy.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Right Column: The Form */}
        <div className="lg:w-1/2 p-8 lg:p-16 bg-white/[0.02]">
          <div className="max-w-md mx-auto bg-[#0a0a0a] border border-white/10 p-8 rounded-xl shadow-2xl">
            <h2 className="text-xl font-semibold text-white mb-6">Talk to Sales</h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-400">First Name *</label>
                  <Input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Jane"
                    className="bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-400">Last Name *</label>
                  <Input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Doe"
                    className="bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-400">Work Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="jane@company.com"
                  className="bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-purple-500 focus:ring-purple-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-400">Company Name *</label>
                <Input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                  placeholder="Acme Corp"
                  className="bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-purple-500 focus:ring-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-400">Company Size *</label>
                  <div className="relative">
                    <select
                      name="company_size"
                      value={formData.company_size}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm appearance-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-colors text-gray-300"
                    >
                      <option value="" disabled>Select size</option>
                      <option value="1-50">1-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-1000">201-1,000 employees</option>
                      <option value="1000+">1,000+ employees</option>
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-3 top-3 text-gray-500 pointer-events-none" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-400">Team Function *</label>
                  <div className="relative">
                    <select
                      name="team_function"
                      value={formData.team_function}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm appearance-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-colors text-gray-300"
                    >
                      <option value="" disabled>Role</option>
                      <option value="Product">Product</option>
                      <option value="Engineering">Engineering</option>
                      <option value="Executive / VP">Executive / VP</option>
                      <option value="Procurement">Procurement</option>
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-3 top-3 text-gray-500 pointer-events-none" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-400">What is your biggest blocker? *</label>
                <div className="relative">
                  <select
                    name="biggest_blocker"
                    value={formData.biggest_blocker}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm appearance-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-colors text-gray-300"
                  >
                    <option value="" disabled>Choose one...</option>
                    <option value="Too much time writing specs">Too much time writing specs</option>
                    <option value="Executive visibility/reporting">Executive visibility/reporting</option>
                    <option value="Misalignment between Product & Eng">Misalignment between Product & Eng</option>
                    <option value="Security & Compliance requirements">Security & Compliance requirements</option>
                    <option value="Other">Other</option>
                  </select>
                  <ChevronDown className="w-4 h-4 absolute right-3 top-3 text-gray-500 pointer-events-none" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-400">Additional Details (Optional)</label>
                <Textarea
                  name="additional_details"
                  value={formData.additional_details}
                  onChange={handleInputChange}
                  placeholder="Tell us about your team's current stack..."
                  className="bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-purple-500 focus:ring-purple-500 min-h-[100px]"
                />
              </div>

              {error && (
                <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-md p-3">
                  {error}
                </div>
              )}

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-medium py-3 shadow-lg shadow-purple-500/20"
              >
                {isSubmitting ? 'Submitting...' : 'Contact Sales'}
              </Button>

              <p className="text-[10px] text-gray-500 text-center pt-2">
                By contacting sales, you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Privacy Policy</a>.
              </p>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactSalesPage;
